import { useEffect, useState } from "react";
import { academyVersionRequest, createVersionedCommandSender } from "./academyApi";
import type {
  InstructorAssessmentAssignmentListItem,
  InstructorExamAssessmentAssignmentReceipt,
  InstructorExamAssessmentAssignmentRequest,
  InstructorAssessmentAssessorAssignmentReceipt,
  InstructorAssessmentAssessorAssignmentRequest,
  InstructorAssessmentAssessorManagementItem,
  InstructorAssessmentEvaluationQueueItem,
  InstructorAssessmentEvaluationReceipt,
  InstructorAssessmentEvaluationRequest,
  InstructorAssessmentIntegrityReferralReceipt,
  InstructorAssessmentIntegrityReferralRequest,
  InstructorAssessmentModerationQueueItem,
  InstructorAssessmentModerationReceipt,
  InstructorAssessmentModerationRequest,
  InstructorAssessmentModeratorClaimReceipt,
  InstructorAssessmentOfficialResultQueueItem,
  InstructorAssessmentOfficialResultReceipt,
  InstructorAssessmentOfficialResultRequest,
  InstructorScoringPolicyAuthoringItem,
  InstructorScoringPolicyProposalReceipt,
  InstructorScoringPolicyProposalRequest,
  InstructorScoringPolicyProposal,
  InstructorScoringPolicyReviewReceipt,
  InstructorScoringPolicyReviewRequest,
} from "./generated/instructor-assessment";

const sendPolicyCommand = createVersionedCommandSender("d11-scoring-policy");
const sendEvaluationCommand = createVersionedCommandSender("d11-assessment-evaluation");
const sendIntegrityReferralCommand = createVersionedCommandSender("d11-assessment-integrity-referral");
const sendAssessorCommand = createVersionedCommandSender("d11-assessment-assessor");
const sendExamAssignmentCommand = createVersionedCommandSender("d11-exam-assessment-assignment");
const sendModerationCommand = createVersionedCommandSender("d11-assessment-moderation");
const sendResultCommand = createVersionedCommandSender("d11-assessment-official-result");

export function useInstructorAssessmentResultPublicationQueue() {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    items: InstructorAssessmentOfficialResultQueueItem[];
    error: string;
  }>();
  useEffect(() => {
    let active = true;
    void academyVersionRequest<InstructorAssessmentOfficialResultQueueItem[]>(
      "v3/instructor/assessment/result-publication-queue",
      undefined,
      "GET",
    )
      .then(items => {
        if (active) setState({ items, error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Official result publication is unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [revision]);
  return {
    items: state?.items ?? [],
    error: state?.error ?? "",
    loading: !state,
    refresh: () => setRevision(value => value + 1),
  };
}

export function publishInstructorAssessmentOfficialResult(
  moderationDecisionId: string,
  request: InstructorAssessmentOfficialResultRequest,
) {
  return sendResultCommand<InstructorAssessmentOfficialResultReceipt>(
    `v3/instructor/assessment/moderation-decisions/${moderationDecisionId}/official-results`,
    request,
  );
}

export function useInstructorAssessmentModerationQueue() {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    items: InstructorAssessmentModerationQueueItem[];
    error: string;
  }>();
  useEffect(() => {
    let active = true;
    void academyVersionRequest<InstructorAssessmentModerationQueueItem[]>(
      "v3/instructor/assessment/moderation-queue",
      undefined,
      "GET",
    )
      .then(items => {
        if (active) setState({ items, error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Assessment moderation is unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [revision]);
  return {
    items: state?.items ?? [],
    error: state?.error ?? "",
    loading: !state,
    refresh: () => setRevision(value => value + 1),
  };
}

export function claimInstructorAssessmentModeration(evaluationId: string) {
  return sendModerationCommand<InstructorAssessmentModeratorClaimReceipt>(
    `v3/instructor/assessment/evaluations/${evaluationId}/moderator-claims`,
    {},
  );
}

export function finalizeInstructorAssessmentModeration(
  evaluationId: string,
  request: InstructorAssessmentModerationRequest,
) {
  return sendModerationCommand<InstructorAssessmentModerationReceipt>(
    `v3/instructor/assessment/evaluations/${evaluationId}/moderation-decisions`,
    request,
    request.expectedEvaluationVersion,
  );
}

export function useInstructorAssessmentAssessorManagement(assessmentAssignmentId: string, enabled: boolean) {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    items: InstructorAssessmentAssessorManagementItem[];
    error: string;
  }>();
  useEffect(() => {
    if (!enabled) {
      setState({ items: [], error: "" });
      return;
    }
    let active = true;
    void academyVersionRequest<InstructorAssessmentAssessorManagementItem[]>(
      `v3/instructor/assessment/assignments/${assessmentAssignmentId}/assessors`,
      undefined,
      "GET",
    )
      .then(items => {
        if (active) setState({ items, error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Assessor management is unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [assessmentAssignmentId, enabled, revision]);
  return {
    items: state?.items ?? [],
    error: state?.error ?? "",
    loading: enabled && !state,
    refresh: () => setRevision(value => value + 1),
  };
}

export function assignInstructorAssessmentAssessor(
  assessmentAssignmentId: string,
  request: InstructorAssessmentAssessorAssignmentRequest,
) {
  return sendAssessorCommand<InstructorAssessmentAssessorAssignmentReceipt>(
    `v3/instructor/assessment/assignments/${assessmentAssignmentId}/assessors`,
    request,
  );
}

export function publishInstructorExamAssessmentAssignment(
  assessmentPublicationId: string,
  request: InstructorExamAssessmentAssignmentRequest,
) {
  return sendExamAssignmentCommand<InstructorExamAssessmentAssignmentReceipt>(
    `v3/instructor/assessment/publications/${assessmentPublicationId}/exam-assignments`,
    request,
  );
}

export function useInstructorAssessmentEvaluationQueue() {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    items: InstructorAssessmentEvaluationQueueItem[];
    error: string;
  }>();
  useEffect(() => {
    let active = true;
    void academyVersionRequest<InstructorAssessmentEvaluationQueueItem[]>(
      "v3/instructor/assessment/evaluation-queue",
      undefined,
      "GET",
    )
      .then(items => {
        if (active) setState({ items, error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Assessment evaluations are unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [revision]);
  return {
    items: state?.items ?? [],
    error: state?.error ?? "",
    loading: !state,
    refresh: () => setRevision(value => value + 1),
  };
}

export function finalizeInstructorAssessmentEvaluation(
  attemptId: string,
  request: InstructorAssessmentEvaluationRequest,
) {
  return sendEvaluationCommand<InstructorAssessmentEvaluationReceipt>(
    `v3/instructor/assessment/attempts/${attemptId}/evaluations`,
    request,
  );
}

export function referInstructorAssessmentIntegrity(
  attemptId: string,
  request: InstructorAssessmentIntegrityReferralRequest,
) {
  return sendIntegrityReferralCommand<InstructorAssessmentIntegrityReferralReceipt>(
    `v3/instructor/assessment/attempts/${attemptId}/integrity-referrals`,
    request,
  );
}

export function useInstructorAssessmentAssignments() {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    items: InstructorAssessmentAssignmentListItem[];
    error: string;
  }>();
  useEffect(() => {
    let active = true;
    void academyVersionRequest<InstructorAssessmentAssignmentListItem[]>(
      "v3/instructor/assessment/assignments",
      undefined,
      "GET",
    )
      .then(items => {
        if (active) setState({ items, error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Assessment assignments are unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [revision]);
  return {
    items: state?.items ?? [],
    error: state?.error ?? "",
    loading: !state,
    refresh: () => setRevision(value => value + 1),
  };
}

export function submitInstructorScoringPolicy(
  publicationId: string,
  request: InstructorScoringPolicyProposalRequest,
) {
  return sendPolicyCommand<InstructorScoringPolicyProposalReceipt>(
    `v3/instructor/assessment/publications/${publicationId}/scoring-policy-proposals`,
    request,
  );
}

export function reviewInstructorScoringPolicy(
  proposalId: string,
  request: InstructorScoringPolicyReviewRequest,
) {
  return sendPolicyCommand<InstructorScoringPolicyReviewReceipt>(
    `v3/instructor/assessment/scoring-policy/proposals/${proposalId}/review`,
    request,
    request.expectedVersion,
  );
}

export function useInstructorAssessmentPolicies() {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    items: InstructorScoringPolicyAuthoringItem[];
    error: string;
  }>();
  useEffect(() => {
    let active = true;
    void academyVersionRequest<InstructorScoringPolicyAuthoringItem[]>(
      "v3/instructor/assessment/scoring-policy/authoring-queue",
      undefined,
      "GET",
    )
      .then(items => {
        if (active) setState({ items, error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Scoring policies are unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [revision]);
  return {
    items: state?.items ?? [],
    error: state?.error ?? "",
    loading: !state,
    refresh: () => setRevision(value => value + 1),
  };
}

export function useInstructorScoringPolicyReviews() {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{
    items: InstructorScoringPolicyProposal[];
    error: string;
  }>();
  useEffect(() => {
    let active = true;
    void academyVersionRequest<InstructorScoringPolicyProposal[]>(
      "v3/instructor/assessment/scoring-policy/review-queue",
      undefined,
      "GET",
    )
      .then(items => {
        if (active) setState({ items, error: "" });
      })
      .catch(cause => {
        if (active)
          setState({
            items: [],
            error: cause instanceof Error ? cause.message : "Policy reviews are unavailable",
          });
      });
    return () => {
      active = false;
    };
  }, [revision]);
  return {
    items: state?.items ?? [],
    error: state?.error ?? "",
    loading: !state,
    refresh: () => setRevision(value => value + 1),
  };
}
